import { OpenShift } from "@clipboard-health/contract-worker-app-bff";
import { formatWeekdayName } from "@clipboard-health/date-time";
import { isDefined } from "@clipboard-health/util-ts";
import { APP_V2_APP_EVENTS, logError } from "@src/appV2/lib/analytics";
import { parseISO, subDays } from "date-fns";
import { useMemo } from "react";

import { CbhFeatureFlag } from "../../../FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "../../../FeatureFlags/useCbhFlag";
import { useLogEffect } from "../../../lib/analytics/useLogEffect";
import { useDefinedWorker } from "../../../Worker/useDefinedWorker";
import type { OpenShiftWorkplace } from "../../Shift/Open/types";
import {
  ShiftType,
  usePaginatedWorkerShifts,
} from "../../WorkerShifts/api/usePaginatedWorkerShifts";

const RECENT_SHIFTS_LOOKBACK_DAYS = 60;
const DEFAULT_TIMEZONE = "America/Los_Angeles";

function buildLookupStructuresFromPastShifts(
  shifts: ShiftType[],
  workplacesMap: Map<string, OpenShiftWorkplace>
) {
  const tier1KeySet = new Set<string>();
  const tier2KeySet = new Set<string>();
  const pastWorkplaces = new Set<string>();
  const recentWorkplaceCount: Record<string, number> = {};
  const recentTier1Count: Record<string, number> = {};
  const recentTier2Count: Record<string, number> = {};

  for (const shift of shifts) {
    const schedule = shift.attributes?.schedule;
    if (!isDefined(schedule)) {
      logError(APP_V2_APP_EVENTS.FOR_YOU_SHIFTS_PAST_SHIFT_MISSING_SCHEDULE, {
        error: new Error("Past shift is missing schedule attribute"),
        metadata: { shiftId: shift.id },
      });
      continue;
    }
    const { startAt, timeSlot } = schedule;
    const workplaceId = shift.relationships?.workplace?.data?.id;

    if (!isDefined(workplaceId) || !isDefined(startAt) || !isDefined(timeSlot)) {
      logError(APP_V2_APP_EVENTS.FOR_YOU_SHIFTS_PAST_SHIFT_MISSING_REQUIRED_FIELDS, {
        error: new Error("Past shift is missing workplaceId, startAt, or timeSlot"),
        metadata: { shiftId: shift.id },
      });
      continue;
    }
    const workplaceTimezone =
      workplacesMap.get(workplaceId)?.attributes.location.timezone ?? DEFAULT_TIMEZONE;
    const dayOfWeek = formatWeekdayName(parseISO(startAt), { timeZone: workplaceTimezone });

    const tier1Key = `${workplaceId}_${dayOfWeek}_${timeSlot}`;
    const tier2Key = `${workplaceId}_${timeSlot}`;

    tier1KeySet.add(tier1Key);
    tier2KeySet.add(tier2Key);
    pastWorkplaces.add(workplaceId);

    recentWorkplaceCount[workplaceId] = (recentWorkplaceCount[workplaceId] ?? 0) + 1;
    recentTier1Count[tier1Key] = (recentTier1Count[tier1Key] ?? 0) + 1;
    recentTier2Count[tier2Key] = (recentTier2Count[tier2Key] ?? 0) + 1;
  }

  return {
    tier1KeySet,
    tier2KeySet,
    pastWorkplaces,
    recentWorkplaceCount,
    recentTier1Count,
    recentTier2Count,
  };
}

export function useForYouShifts(params: {
  shifts: OpenShift[];
  workplacesMap: Map<string, OpenShiftWorkplace>;
  isOpenShiftsSuccess: boolean;
}) {
  const { shifts, workplacesMap, isOpenShiftsSuccess } = params;
  const isForYouShiftsEnabled = useCbhFlag(CbhFeatureFlag.TOP_PICKED_SHIFTS_FOR_YOU, {
    defaultValue: false,
  });
  const worker = useDefinedWorker();
  const workerId = worker.userId;
  const pastShiftsCutoffDate = useMemo(() => subDays(new Date(), RECENT_SHIFTS_LOOKBACK_DAYS), []);
  const pastShiftsQuery = usePaginatedWorkerShifts(
    {
      workerId,
      timeline: "PAST",
      pageSize: 100,
      include: ["workplace"],
      fields: {
        shift: ["schedule.startAt", "schedule.endAt", "schedule.timeSlot"],
      },
      filter: { start: { gt: pastShiftsCutoffDate } },
    },
    { enabled: isForYouShiftsEnabled }
  );

  const pastShifts = useMemo(
    () => pastShiftsQuery?.data?.pages.flatMap((page) => page.data),
    [pastShiftsQuery?.data?.pages]
  );

  const lookupStructures = useMemo(
    () => buildLookupStructuresFromPastShifts(pastShifts ?? [], workplacesMap),
    [pastShifts, workplacesMap]
  );

  const {
    tier1KeySet,
    tier2KeySet,
    pastWorkplaces,
    recentWorkplaceCount,
    recentTier1Count,
    recentTier2Count,
  } = lookupStructures;

  const filteredShifts = useMemo(() => {
    return shifts.filter((shift) => pastWorkplaces.has(shift.relationships.workplace.data.id));
  }, [shifts, pastWorkplaces]);

  const forYouShifts = useMemo(() => {
    function getPriority(shift: OpenShift, workplacesMap: Map<string, OpenShiftWorkplace>) {
      const { start, timeSlot } = shift.attributes;
      const workplaceId = shift.relationships.workplace.data.id;
      const workplaceTimezone =
        workplacesMap.get(workplaceId)?.attributes.location.timezone ?? DEFAULT_TIMEZONE;
      const dayOfWeek = formatWeekdayName(parseISO(start), { timeZone: workplaceTimezone });

      const tier1Key = `${workplaceId}_${dayOfWeek}_${timeSlot}`;
      const tier2Key = `${workplaceId}_${timeSlot}`;

      if (tier1KeySet.has(tier1Key)) {
        return 3;
      } else if (tier2KeySet.has(tier2Key)) {
        return 2;
      }

      return 1;
    }

    function getTiebreakerCount(
      shift: OpenShift,
      priority: number,
      workplacesMap: Map<string, OpenShiftWorkplace>
    ) {
      const { start, timeSlot } = shift.attributes;
      const workplaceId = shift.relationships.workplace.data.id;

      if (priority === 3) {
        const workplaceTimezone =
          workplacesMap.get(workplaceId)?.attributes.location.timezone ?? DEFAULT_TIMEZONE;
        const dayOfWeek = formatWeekdayName(parseISO(start), { timeZone: workplaceTimezone });
        return recentTier1Count[`${workplaceId}_${dayOfWeek}_${timeSlot}`] ?? 0;
      }

      if (priority === 2) {
        return recentTier2Count[`${workplaceId}_${timeSlot}`] ?? 0;
      }

      return recentWorkplaceCount[workplaceId] ?? 0;
    }

    function sortShifts(shifts: OpenShift[], workplacesMap: Map<string, OpenShiftWorkplace>) {
      return shifts.toSorted((a, b) => {
        const priorityA = getPriority(a, workplacesMap);
        const priorityB = getPriority(b, workplacesMap);
        const priorityDiff = priorityB - priorityA;
        if (priorityDiff !== 0) {
          return priorityDiff;
        }

        const countDiff =
          getTiebreakerCount(b, priorityB, workplacesMap) -
          getTiebreakerCount(a, priorityA, workplacesMap);
        if (countDiff !== 0) {
          return countDiff;
        }

        const createdAtA = (a.attributes as { createdAt?: string }).createdAt;
        const createdAtB = (b.attributes as { createdAt?: string }).createdAt;
        if (isDefined(createdAtA) && isDefined(createdAtB)) {
          return parseISO(createdAtB).getTime() - parseISO(createdAtA).getTime();
        }

        return 0;
      });
    }

    const sortedShifts = sortShifts(filteredShifts, workplacesMap);
    const topShifts = sortedShifts.slice(0, 3);
    return topShifts.toSorted((a, b) => {
      return parseISO(a.attributes.start).getTime() - parseISO(b.attributes.start).getTime();
    });
  }, [
    filteredShifts,
    workplacesMap,
    tier1KeySet,
    tier2KeySet,
    recentWorkplaceCount,
    recentTier1Count,
    recentTier2Count,
  ]);

  useLogEffect(APP_V2_APP_EVENTS.FOR_YOU_SHIFTS_NO_SHIFTS_SELECTED, undefined, {
    enabled: isOpenShiftsSuccess && isForYouShiftsEnabled && forYouShifts.length === 0,
  });

  if (!isForYouShiftsEnabled) {
    return [];
  }

  return forYouShifts;
}
